import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ModalProvider } from './contexts/ModalContext';
import { RequireAuth } from './components/RequireAuth';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/Login';
import { UnauthorizedPage } from './pages/Unauthorized';
import { NotFoundPage } from './pages/NotFound';
import { SectionStub } from './pages/SectionStub';
import { SpecialistVerificationPage } from './pages/SpecialistVerification';
import { TrustSafetyPage } from './pages/TrustSafety';
import { PaymentsFinancePage } from './pages/PaymentsFinance';
import { UserManagementPage } from './pages/UserManagement';
import { ReferralLandingPage } from './pages/ReferralLanding';
import { SystemOpsPage } from './pages/SystemOps';
import { AuditLogPage } from './pages/AuditLog';
import { PORTAL_SECTIONS, type PortalSectionId } from './lib/roles';

import { DashboardPage } from './pages/Dashboard';
import { CaseManagementPage } from './pages/CaseManagement';
import { ReferralOperationsPage } from './pages/referral-operations/ReferralOperations';
import { RolesPermissionsPage } from './pages/RolesPermissions';
import { FeatureAnalyticsPage } from './pages/FeatureAnalytics';
import { FeatureAnalyticsDetailPage } from './pages/FeatureAnalyticsDetail';
import { ServiceCardsPage } from './pages/ServiceCards';
import { SpecialtiesPage } from './pages/Specialties';
import { ServiceRulesPage } from './pages/ServiceRules';
import { PoolScoreConfigPage } from './pages/PoolScoreConfig';
import { OperationalSettingsPage } from './pages/OperationalSettings';
import { ModerationQueuePage } from './pages/ModerationQueue';
import { PlansPage } from './pages/Plans';
import { CarePlansPage } from './pages/CarePlans';
import { PromptStudioPage } from './pages/prompt-studio/PromptStudio';
import { EmailTemplatesPage } from './pages/email-templates/EmailTemplates';
import { PoolRankingsPage } from './pages/PoolRankings';
import { LynkAlertOpsPage } from './pages/LynkAlertOps';
import { ServiceCardTemplatesPage } from './pages/ServiceCardTemplates';
import { SurfaceControlsPage } from './pages/SurfaceControls';
import { LegalDocumentsPage } from './pages/LegalDocuments';
import { ServiceAnalyticsPage } from './pages/ServiceAnalytics';
import { MailPage } from './pages/Mail';
import { MailAdminPage } from './pages/MailAdmin';

const SECTION_COMPONENTS: Partial<Record<PortalSectionId, () => JSX.Element>> = {
  'specialist-verification': SpecialistVerificationPage,
  'trust-safety': TrustSafetyPage,
  'payments-finance': PaymentsFinancePage,
  'user-management': UserManagementPage,
  'dashboard': DashboardPage,
  'case-management': CaseManagementPage,
  'referral-operations': ReferralOperationsPage,
  'system-ops': SystemOpsPage,
  'audit-log': AuditLogPage,
  'roles-permissions': RolesPermissionsPage,
  'feature-analytics': FeatureAnalyticsPage,
  'service-cards': ServiceCardsPage,
  'specialties': SpecialtiesPage,
  'service-rules': ServiceRulesPage,
  'pool-score-config': PoolScoreConfigPage,
  'pool-rankings': PoolRankingsPage,
  'lynk-alert-ops': LynkAlertOpsPage,
  'service-card-templates': ServiceCardTemplatesPage,
  'operational-settings': OperationalSettingsPage,
  'moderation-queue': ModerationQueuePage,
  'prompt-studio': PromptStudioPage,
  'surface-controls': SurfaceControlsPage,
  'plans': PlansPage,
  'legal-documents': LegalDocumentsPage,
  'care-plans': CarePlansPage,
  'service-analytics': ServiceAnalyticsPage,
  'mail': MailPage,
  'mail-admin': MailAdminPage,
  'email-templates': EmailTemplatesPage,
};

const FEATURE_ANALYTICS_SECTION = PORTAL_SECTIONS.find((s) => s.id === 'feature-analytics')!;

export function App() {
  return (
    <BrowserRouter>
      <ModalProvider>
      <AuthProvider>
        <Routes>
          {/* Public routes — no auth required */}
          <Route path="/ref/:code" element={<ReferralLandingPage />} />

          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          <Route element={<RequireAuth />}>
            <Route element={<Layout />}>
              {PORTAL_SECTIONS.map((section) => {
                const SectionComponent = SECTION_COMPONENTS[section.id];
                return (
                  <Route
                    key={section.id}
                    path={section.path}
                    element={
                      <ProtectedRoute
                        allowedRoles={section.roles}
                        requiredPermission={section.requiredPermission}
                      >
                        {SectionComponent ? <SectionComponent /> : <SectionStub section={section} />}
                      </ProtectedRoute>
                    }
                  />
                );
              })}
              <Route
                path="/analytics/:featureId"
                element={
                  <ProtectedRoute
                    allowedRoles={FEATURE_ANALYTICS_SECTION.roles}
                    requiredPermission={FEATURE_ANALYTICS_SECTION.requiredPermission}
                  >
                    <FeatureAnalyticsDetailPage />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
      </ModalProvider>
    </BrowserRouter>
  );
}
